import { useState } from 'react';
import Icon from '../components/ui/Icon.jsx';
import { useApi } from '../hooks/useApi.js';
import { getDartCards, getDartCardActivationSteps } from '../api/dartCardApi.js';
import { useCart } from '../context/CartContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { CARD_BRANDS, CARD_BRAND_LABELS } from '../data/dart-cards.js';
import { analyticsEvent, gaItem, trackFilter } from '../utils/analytics.js';
import './DartCardPage.css';

const FILTER_ALL = 'ALL';

const FILTERS = [
  { value: FILTER_ALL,            label: '全部' },
  { value: CARD_BRANDS.DARTSLIVE, label: CARD_BRAND_LABELS[CARD_BRANDS.DARTSLIVE] },
  { value: CARD_BRANDS.PHOENICA,  label: CARD_BRAND_LABELS[CARD_BRANDS.PHOENICA] },
];

function DartCardPage() {
  const [brand, setBrand] = useState(FILTER_ALL);
  const { data: cards = [], loading } = useApi(() => getDartCards(), []);
  const { data: steps = [] }          = useApi(() => getDartCardActivationSteps(), []);

  const filtered = brand === FILTER_ALL ? cards : cards.filter(c => c.brand === brand);

  return (
    <main className="dc">
      <header className="dc-head">
        <p className="dc-eyebrow">PHYSICAL CARD · #07</p>
        <h1 className="dc-title">飛鏢機卡 · <em>DARTSLIVE</em> &amp; PHOENICA</h1>
        <p className="dc-sub">
          插入機台即可記錄戰績、雲端同步個人設定、解鎖主題卡面。
          線上下單寄送實體卡，依官方流程綁定 Player ID 即可開戰。
        </p>
      </header>

      <div className="dc-filters" role="tablist">
        {FILTERS.map(f => (
          <button
            key={f.value}
            role="tab"
            aria-selected={brand === f.value}
            className={`dc-filter${brand === f.value ? ' on' : ''}`}
            onPointerDown={() => trackFilter('brand', f.value, 'dart_cards')}
            onClick={() => setBrand(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="dc-grid">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <DCSkeleton key={i} />)
          : filtered.map(c => <DCard key={c.id} card={c} />)
        }
      </div>

      {steps.length > 0 && (
        <section className="dc-how">
          <h3 className="dc-how-title">啟用流程 · ACTIVATION</h3>
          <div className="dc-how-grid">
            {steps.map(s => (
              <div className="dc-step" key={s.num}>
                <span className="dc-step-num">{s.num}</span>
                <div>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function DCard({ card }) {
  const { addToCart } = useCart();
  const { showToast } = useToast();
  const soldOut = card.stock <= 0;

  const handleBuy = () => {
    if (soldOut) return;
    addToCart({
      id: card.id,
      name: card.name,
      price: card.price,
      image: null,
      kind: 'dart-card',
    });
    analyticsEvent('add_to_cart', {
      currency: 'TWD',
      value: Number(card.price ?? 0),
      items: [gaItem({ ...card, category: 'dart_card' }, 0)],
    });
    showToast(`已加入購物車：${card.name}`, 'success');
  };

  const isPhoenix = card.brand === CARD_BRANDS.PHOENICA;
  const previewTheme = card.faceTheme || 'default';

  return (
    <article className="dc-card">
      <div className={`dc-preview ${previewTheme}`}>
        <div className={`dc-face ${previewTheme}`}>
          <div className="dc-face-row">
            <span className="dc-face-brand">{isPhoenix ? 'PHOENICA' : 'DARTSLIVE'}</span>
            <span className="dc-face-chip" />
          </div>
          <div className="dc-face-no">5183 · 2024 · 0042</div>
          <div className="dc-face-row">
            <span className="dc-face-brand">{card.cardModel || 'YOUR NAME'}</span>
            <span className="dc-face-tier">{isPhoenix ? 'PHX' : 'D3'}</span>
          </div>
        </div>

        {card.tags?.includes('new') && (
          <div className="dc-badges">
            <span className="dc-badge new">NEW</span>
          </div>
        )}
        {card.tags?.includes('limited') && (
          <div className="dc-badges" style={{ left: card.tags?.includes('new') ? 'auto' : 10 }}>
            <span className="dc-badge limit">LIMITED</span>
          </div>
        )}

        <span className={`dc-machine${isPhoenix ? ' phoenix' : ''}`}>
          {isPhoenix ? 'PHOENIX' : 'DARTSLIVE'}
        </span>
      </div>

      <div className="dc-info">
        <h3>{card.name}</h3>
        <span className="dc-info-en">{card.en}</span>

        <div className="dc-features">
          {card.features.map((f, i) => (
            <div key={i}><Icon name="check" size={12} /> <span>{f}</span></div>
          ))}
        </div>

        {card.bundledThemeCount > 0 && (
          <div className="dc-bundle">
            <Icon name="trophy" size={12} />
            附贈 {card.bundledThemeCount} 款虛擬主題 <small style={{ color: 'var(--ink-soft)', fontWeight: 600 }}>· 啟用後自動掛載到你的 Player ID</small>
          </div>
        )}

        <div className="dc-stock">
          <span>庫存</span>
          <b className={card.stock < 10 ? 'low' : ''}>{soldOut ? '已售完' : `${card.stock} 張`}</b>
        </div>

        <div className="dc-foot">
          <span className="dc-price"><small>NT$</small>{card.price.toLocaleString()}</span>
          <button className="dc-buy" onClick={handleBuy} disabled={soldOut}>
            {soldOut ? '已售完' : '加入購物車'}
          </button>
        </div>
      </div>
    </article>
  );
}

function DCSkeleton() {
  return (
    <article className="dc-card">
      <div className="dc-preview" style={{ background: 'var(--ice)' }} />
      <div className="dc-info" style={{ padding: '4px 0' }}>
        <div style={{ height: 18, background: 'var(--ice)', borderRadius: 4, marginBottom: 10 }} />
        <div style={{ height: 12, background: 'var(--ice)', borderRadius: 4, width: '60%' }} />
      </div>
    </article>
  );
}

export default DartCardPage;
